const DEFAULT_MARKS = {
  weight: "normal",
  style: "normal",
  color: "#1f2937",
  fontSize: 17,
};

export const DEFAULT_PARAGRAPH_ALIGN = "justify";

export const WCP_HARD_BREAK_ATTR = "data-wcp-hard-break";
export const WCP_HARD_BREAK_VALUE = "1";
export const WCP_LINE_ALIGN_ATTR = "data-wcp-line-align";
export const WCP_TAB_ATTR = "data-wcp-tab";
export const HARD_BREAK_ATTR = WCP_HARD_BREAK_ATTR;

export const defaultTextMarks = () => ({ ...DEFAULT_MARKS });

export const createEmptyRun = () => ({
  text: "",
  marks: defaultTextMarks(),
});

export const normalizeFontSize = (value) => {
  if (value == null || value === "") {
    return undefined;
  }
  const parsed = typeof value === "number" ? value : Number.parseInt(String(value).replace(/px$/i, ""), 10);
  if (!Number.isFinite(parsed) || parsed === DEFAULT_MARKS.fontSize) {
    return undefined;
  }
  return parsed;
};

export const normalizeParagraphAlign = (align) => {
  if (align === "left" || align === "center" || align === "justify") {
    return align === DEFAULT_PARAGRAPH_ALIGN ? undefined : align;
  }
  return undefined;
};

export const normalizeLineAlignValue = (align) => {
  if (align === "left" || align === "center" || align === "justify") {
    return align;
  }
  return undefined;
};

export const splitRunsIntoLines = (runs) => {
  const lines = [[]];
  mergeAdjacentRuns(runs).forEach((run) => {
    const chunks = String(run.text ?? "").split("\n");
    chunks.forEach((chunk, chunkIndex) => {
      if (chunk) {
        lines[lines.length - 1].push({
          text: chunk,
          marks: {
            ...defaultTextMarks(),
            ...(run.marks || {}),
          },
        });
      }
      if (chunkIndex < chunks.length - 1) {
        lines.push([]);
      }
    });
  });
  return lines.length ? lines : [[]];
};

export const resolveLineAlign = (block, lineIndex) => {
  const paragraphAlign =
    block?.align === "left" || block?.align === "center" || block?.align === "justify"
      ? block.align
      : DEFAULT_PARAGRAPH_ALIGN;
  const lineValue = normalizeLineAlignValue(block?.lineAligns?.[lineIndex]);
  return lineValue || paragraphAlign;
};

export const usesLineAlignLayout = (block) => {
  const normalized = normalizeParagraphBlock(block);
  const lineCount = splitRunsIntoLines(normalized.runs).length;
  if (lineCount > 1) {
    return true;
  }
  return Array.isArray(normalized.lineAligns)
    && normalized.lineAligns.some((value) => normalizeLineAlignValue(value));
};

export const compactLineAligns = (lineAligns, lineCount, defaultAlign) => {
  if (!Array.isArray(lineAligns) || lineCount <= 0) {
    return undefined;
  }
  const compacted = [];
  let hasOverride = false;
  for (let index = 0; index < lineCount; index += 1) {
    const value = normalizeLineAlignValue(lineAligns[index]);
    if (value && value !== defaultAlign) {
      compacted[index] = value;
      hasOverride = true;
    }
  }
  return hasOverride ? compacted : undefined;
};

export const normalizeParagraphBlock = (block) => {
  if (!block || block.type !== "paragraph") {
    return block;
  }
  const base = {
    type: "paragraph",
    ...(block.align ? { align: block.align } : {}),
    ...(Array.isArray(block.lineAligns) ? { lineAligns: [...block.lineAligns] } : {}),
  };
  if (Array.isArray(block.runs) && block.runs.length > 0) {
    return {
      ...base,
      runs: block.runs.map((run) => ({
        text: run.text ?? "",
        marks: {
          ...defaultTextMarks(),
          ...(run.marks || {}),
        },
      })),
    };
  }
  if (block.text !== undefined) {
    return {
      ...base,
      runs: [
        {
          text: block.text,
          marks: {
            ...defaultTextMarks(),
            ...(block.marks || {}),
          },
        },
      ],
    };
  }
  return {
    ...base,
    runs: [createEmptyRun()],
  };
};

const marksEqual = (left, right) =>
  (left?.weight || "normal") === (right?.weight || "normal")
  && (left?.style || "normal") === (right?.style || "normal")
  && (left?.color || DEFAULT_MARKS.color) === (right?.color || DEFAULT_MARKS.color)
  && (left?.fontSize || DEFAULT_MARKS.fontSize) === (right?.fontSize || DEFAULT_MARKS.fontSize);

export const mergeAdjacentRuns = (runs) => {
  const merged = [];
  runs.forEach((run) => {
    if (!run.text) {
      return;
    }
    const normalized = {
      text: run.text,
      marks: {
        ...defaultTextMarks(),
        ...(run.marks || {}),
      },
    };
    const last = merged[merged.length - 1];
    if (last && marksEqual(last.marks, normalized.marks)) {
      last.text += normalized.text;
      return;
    }
    merged.push(normalized);
  });
  return merged.length ? merged : [createEmptyRun()];
};

const hardBreakHtml = () => `<br ${WCP_HARD_BREAK_ATTR}="${WCP_HARD_BREAK_VALUE}">`;

const editorTabMarkerHtml = () => `<span ${WCP_TAB_ATTR}="1">\t</span>`;

const escapeHtml = (value) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("\n", hardBreakHtml());

const escapeEditorInlineHtml = (value) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("\t", editorTabMarkerHtml());

const appendHardBreak = (runs, marks) => {
  const last = runs[runs.length - 1];
  if (last && marksEqual(last.marks, marks)) {
    last.text += "\n";
    return;
  }
  runs.push({
    text: "\n",
    marks: { ...marks },
  });
};

export const isHardBreakElement = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE || node.tagName?.toLowerCase() !== "br") {
    return false;
  }
  if (node.getAttribute(WCP_HARD_BREAK_ATTR) === WCP_HARD_BREAK_VALUE) {
    return true;
  }
  return node.hasAttribute("data-hard-break");
};

export const normalizeRunText = (text) =>
  String(text ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\u200B/g, "")
    .replace(/\u00a0/g, " ")
    .split("\n")
    .map((line) => line.replace(/[^\S\t]{2,}/g, " "))
    .join("\n");

export const stripSoftWrapNewlineArtifacts = (text) =>
  String(text ?? "").replace(/[ \t]+\n(?!\n)/g, " ");

const isBlockContainer = (tag) => tag === "div" || tag === "p";

const blockEndsWithSoftBreak = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  const tag = node.tagName?.toLowerCase();
  if (!isBlockContainer(tag)) {
    return false;
  }
  let last = node.lastChild;
  while (last && last.nodeType === Node.TEXT_NODE && !last.textContent) {
    last = last.previousSibling;
  }
  if (!last || last.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  return last.tagName?.toLowerCase() === "br" && !isHardBreakElement(last);
};

const isBlockContainerElement = (node) =>
  node?.nodeType === Node.ELEMENT_NODE && isBlockContainer(node.tagName?.toLowerCase());

const shouldInsertBlockBoundaryBreak = (previousNode, nextNode) => {
  if (!isBlockContainerElement(previousNode) || !isBlockContainerElement(nextNode)) {
    return false;
  }
  return !blockEndsWithSoftBreak(previousNode);
};

const isBoldFontWeight = (fontWeight) => {
  if (!fontWeight) {
    return false;
  }
  const raw = String(fontWeight).trim().toLowerCase();
  if (raw === "bold" || raw === "bolder") {
    return true;
  }
  const numeric = Number.parseInt(raw, 10);
  return Number.isFinite(numeric) && numeric >= 600;
};

export const normalizeMarkColor = (color) => {
  if (color == null || color === "") {
    return undefined;
  }
  const raw = String(color).trim();
  if (raw.startsWith("#")) {
    const hex = raw.toLowerCase();
    return hex === DEFAULT_MARKS.color ? undefined : hex;
  }
  const match = raw.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!match) {
    return undefined;
  }
  const [, r, g, b] = match;
  const hex = `#${[r, g, b].map((part) => Number.parseInt(part, 10).toString(16).padStart(2, "0")).join("")}`;
  return hex === DEFAULT_MARKS.color ? undefined : hex;
};

const rgbToHex = (color) => normalizeMarkColor(color) || DEFAULT_MARKS.color;

const figmaPxToRem = (figmaPx) => `${(figmaPx * 0.85) / 14}rem`;

const cssFontSizeToFigmaPx = (value) => {
  if (!value) {
    return undefined;
  }
  const raw = String(value).trim().toLowerCase();
  const pxMatch = raw.match(/^([\d.]+)px$/);
  if (pxMatch) {
    return normalizeFontSize(Math.round(Number(pxMatch[1]) / 0.85));
  }
  const remMatch = raw.match(/^([\d.]+)rem$/);
  if (remMatch) {
    return normalizeFontSize(Math.round((Number(remMatch[1]) * 14) / 0.85));
  }
  return normalizeFontSize(Number.parseInt(raw, 10));
};

const runsToInlineHtml = (runs, options = {}) => {
  const escapeInline = options.renderTabs ? escapeEditorInlineHtml : escapeHtml;
  return mergeAdjacentRuns(runs).map((run) => {
    let html = escapeInline(run.text);
    const marks = run.marks || defaultTextMarks();
    const styles = [];
    const color = normalizeMarkColor(marks.color);
    const fontSize = normalizeFontSize(marks.fontSize);
    if (color) {
      styles.push(`color:${color}`);
    }
    if (fontSize) {
      styles.push(`font-size:${figmaPxToRem(fontSize)}`);
    }
    if (styles.length > 0) {
      html = `<span style="${styles.join(";")}">${html}</span>`;
    }
    if (marks.style === "italic") {
      html = `<em>${html}</em>`;
    }
    if (marks.weight === "bold") {
      html = `<strong>${html}</strong>`;
    }
    return html;
  }).join("");
};

const buildLineDivHtml = (lineRuns, align, options = {}) => {
  const inner = runsToInlineHtml(lineRuns, options);
  return `<div ${WCP_LINE_ALIGN_ATTR}="${align}" style="text-align:${align}">${inner}</div>`;
};

/** Editor DOM always uses one div per line for stable Enter/caret behavior. */
export const runsToEditorHtml = (runs, options = {}) => {
  const block = {
    align: options.paragraphAlign,
    lineAligns: options.lineAligns,
  };
  const lines = splitRunsIntoLines(runs);
  const renderOptions = { renderTabs: true };
  return lines
    .map((lineRuns, index) => buildLineDivHtml(lineRuns, resolveLineAlign(block, index), renderOptions))
    .join("");
};

export const runsToHtml = (runs, options = {}) => {
  const block = {
    align: options.paragraphAlign,
    lineAligns: options.lineAligns,
  };
  const lineAligns = Array.isArray(options.lineAligns) ? options.lineAligns : [];
  const lines = splitRunsIntoLines(runs);
  const useLineDivs = lines.length > 1 || lineAligns.some((value) => normalizeLineAlignValue(value));

  if (!useLineDivs) {
    return runsToInlineHtml(runs);
  }

  return lines
    .map((lineRuns, index) => buildLineDivHtml(lineRuns, resolveLineAlign(block, index)))
    .join("");
};

const escapePublicHtml = (value) =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("\n", "<br>")
    .replaceAll("\t", "&#9;");

/** Public/article HTML renderer. Keep in sync with FE_Application articleParagraphRender.js */
export const runsToPublicHtml = (runs) =>
  mergeAdjacentRuns(runs).map((run) => {
    let html = escapePublicHtml(run.text);
    const marks = run.marks || defaultTextMarks();
    const styles = [];
    const color = normalizeMarkColor(marks.color);
    const fontSize = normalizeFontSize(marks.fontSize);
    if (color) {
      styles.push(`color:${color}`);
    }
    if (fontSize) {
      styles.push(`font-size:${figmaPxToRem(fontSize)}`);
    }
    if (styles.length > 0) {
      html = `<span style="${styles.join(";")}">${html}</span>`;
    }
    if (marks.style === "italic") {
      html = `<em>${html}</em>`;
    }
    if (marks.weight === "bold") {
      html = `<strong>${html}</strong>`;
    }
    return html;
  }).join("");

const collectRuns = (node, inheritedMarks, runs) => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = normalizeRunText(node.textContent ?? "");
    if (text) {
      runs.push({
        text,
        marks: { ...inheritedMarks },
      });
    }
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }
  const marks = { ...inheritedMarks };
  const tag = node.tagName?.toLowerCase();
  if (tag === "span" && node.getAttribute(WCP_TAB_ATTR) === "1") {
    runs.push({
      text: "\t",
      marks: { ...marks },
    });
    return;
  }
  if (tag === "br") {
    if (isHardBreakElement(node)) {
      appendHardBreak(runs, marks);
    }
    return;
  }
  if (isBlockContainer(tag)) {
    node.childNodes.forEach((child) => collectRuns(child, marks, runs));
    return;
  }
  if (tag === "b" || tag === "strong") {
    marks.weight = "bold";
  }
  if (tag === "i" || tag === "em") {
    marks.style = "italic";
  }
  if (tag === "font") {
    const fontColor = node.getAttribute("color");
    if (fontColor) {
      marks.color = rgbToHex(fontColor);
    }
  }
  if (node.style?.color) {
    marks.color = rgbToHex(node.style.color);
  }
  if (node.style?.fontSize) {
    const parsedSize = cssFontSizeToFigmaPx(node.style.fontSize);
    if (parsedSize) {
      marks.fontSize = parsedSize;
    }
  }
  if (isBoldFontWeight(node.style?.fontWeight)) {
    marks.weight = "bold";
  }
  if (node.style?.fontStyle === "italic") {
    marks.style = "italic";
  }
  node.childNodes.forEach((child) => collectRuns(child, marks, runs));
};

const htmlToRunsFlat = (element) => {
  if (!element) {
    return [createEmptyRun()];
  }
  const runs = [];
  const childNodes = Array.from(element.childNodes);
  childNodes.forEach((child, index) => {
    if (index > 0 && shouldInsertBlockBoundaryBreak(childNodes[index - 1], child)) {
      appendHardBreak(runs, defaultTextMarks());
    }
    collectRuns(child, defaultTextMarks(), runs);
  });
  return mergeAdjacentRuns(runs);
};

const isLineAlignDiv = (node) =>
  node?.nodeType === Node.ELEMENT_NODE
  && node.tagName?.toLowerCase() === "div"
  && node.hasAttribute(WCP_LINE_ALIGN_ATTR);

const collectLineDivRuns = (lineDiv, runs) => {
  Array.from(lineDiv.childNodes).forEach((child, index) => {
    if (
      lineDiv.childNodes.length === 1
      && child.nodeType === Node.ELEMENT_NODE
      && child.tagName?.toLowerCase() === "br"
    ) {
      return;
    }
    if (index > 0 && shouldInsertBlockBoundaryBreak(lineDiv.childNodes[index - 1], child)) {
      appendHardBreak(runs, defaultTextMarks());
    }
    collectRuns(child, defaultTextMarks(), runs);
  });
};

export const htmlToParagraphEditorContent = (element) => {
  if (!element) {
    return {
      runs: [createEmptyRun()],
      lineAligns: undefined,
    };
  }

  const lineDivs = Array.from(element.children).filter(isLineAlignDiv);
  if (lineDivs.length > 0) {
    const runs = [];
    const lineAligns = [];
    lineDivs.forEach((lineDiv, index) => {
      if (index > 0) {
        appendHardBreak(runs, defaultTextMarks());
      }
      lineAligns[index] = normalizeLineAlignValue(lineDiv.getAttribute(WCP_LINE_ALIGN_ATTR));
      collectLineDivRuns(lineDiv, runs);
    });
    return {
      runs: mergeAdjacentRuns(runs),
      lineAligns,
    };
  }

  return {
    runs: htmlToRunsFlat(element),
    lineAligns: undefined,
  };
};

export const htmlToRuns = (element) => htmlToParagraphEditorContent(element).runs;

export const runsToPlainOffsetText = (runs) =>
  mergeAdjacentRuns(runs).map((run) => run.text).join("");

export const getLineAlignDivs = (editorElement) =>
  Array.from(editorElement?.children ?? []).filter(isLineAlignDiv);

const getLinePlainTextLength = (lineDiv) =>
  runsToPlainOffsetText(htmlToParagraphEditorContent(lineDiv).runs).length;

const placeCaretAtNodeStart = (container, selection) => {
  const range = document.createRange();
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const firstText = walker.nextNode();
  if (firstText) {
    range.setStart(firstText, 0);
  } else {
    range.setStart(container, 0);
  }
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

const placeCaretAtNodeEnd = (container, selection) => {
  const range = document.createRange();
  range.selectNodeContents(container);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

const setCaretOffsetInLineContent = (lineContainer, targetOffset, selection) => {
  let remaining = Math.max(0, targetOffset);
  let result = null;

  const visit = (node) => {
    if (result) {
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = normalizeRunText(node.textContent ?? "");
      if (remaining <= text.length) {
        result = {
          node,
          offset: Math.min(remaining, node.textContent?.length ?? 0),
        };
        return;
      }
      remaining -= text.length;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const tag = node.tagName?.toLowerCase();
    if (tag === "br") {
      return;
    }
    if (isBlockContainer(tag)) {
      node.childNodes.forEach(visit);
      return;
    }
    node.childNodes.forEach(visit);
  };

  lineContainer.childNodes.forEach(visit);
  if (!result) {
    result = { node: lineContainer, offset: lineContainer.childNodes.length };
  }

  const range = document.createRange();
  try {
    range.setStart(result.node, result.offset);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch {
    return false;
  }
};

const setCaretOffsetInLineLayout = (lineDivs, targetOffset, selection) => {
  let remaining = Math.max(0, targetOffset);

  for (let lineIndex = 0; lineIndex < lineDivs.length; lineIndex += 1) {
    if (lineIndex > 0) {
      if (remaining === 0) {
        return placeCaretAtNodeStart(lineDivs[lineIndex], selection);
      }
      remaining -= 1;
    }

    const lineLength = getLinePlainTextLength(lineDivs[lineIndex]);
    if (remaining <= lineLength) {
      return setCaretOffsetInLineContent(lineDivs[lineIndex], remaining, selection);
    }
    remaining -= lineLength;
  }

  return placeCaretAtNodeEnd(lineDivs[lineDivs.length - 1], selection);
};

export const getCaretCharacterOffset = (editorElement) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editorElement?.contains(selection.anchorNode)) {
    return null;
  }
  const range = selection.getRangeAt(0);
  const lineDivs = getLineAlignDivs(editorElement);

  if (lineDivs.length > 0) {
    let offset = 0;
    for (let lineIndex = 0; lineIndex < lineDivs.length; lineIndex += 1) {
      const lineDiv = lineDivs[lineIndex];
      if (lineDiv.contains(range.endContainer) || lineDiv === range.endContainer) {
        const innerRange = document.createRange();
        innerRange.selectNodeContents(lineDiv);
        innerRange.setEnd(range.endContainer, range.endOffset);
        const probe = document.createElement("div");
        probe.appendChild(innerRange.cloneContents());
        offset += runsToPlainOffsetText(htmlToParagraphEditorContent(probe).runs).length;
        return offset;
      }
      offset += getLinePlainTextLength(lineDiv);
      if (lineIndex < lineDivs.length - 1) {
        offset += 1;
      }
    }
    return offset;
  }

  const probe = document.createElement("div");
  const preRange = document.createRange();
  preRange.selectNodeContents(editorElement);
  preRange.setEnd(range.endContainer, range.endOffset);
  probe.appendChild(preRange.cloneContents());
  return runsToPlainOffsetText(htmlToRuns(probe)).length;
};

export const setCaretCharacterOffset = (editorElement, targetOffset) => {
  const selection = window.getSelection();
  if (!selection || !editorElement) {
    return false;
  }

  const lineDivs = getLineAlignDivs(editorElement);
  if (lineDivs.length > 0) {
    return setCaretOffsetInLineLayout(lineDivs, targetOffset, selection);
  }

  let remaining = Math.max(0, targetOffset);
  let result = null;

  const placeAtHardBreak = (node, beforeBreak) => {
    const parent = node.parentNode;
    if (!parent) {
      return;
    }
    const index = Array.prototype.indexOf.call(parent.childNodes, node);
    result = {
      node: parent,
      offset: beforeBreak ? index : index + 1,
    };
  };

  const visit = (node) => {
    if (result) {
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = normalizeRunText(node.textContent ?? "");
      if (remaining <= text.length) {
        result = {
          node,
          offset: Math.min(remaining, node.textContent?.length ?? 0),
        };
        return;
      }
      remaining -= text.length;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const tag = node.tagName?.toLowerCase();
    if (tag === "br") {
      if (isHardBreakElement(node)) {
        if (remaining === 0) {
          placeAtHardBreak(node, true);
          return;
        }
        remaining -= 1;
        if (remaining === 0) {
          placeAtHardBreak(node, false);
        }
      }
      return;
    }
    if (isBlockContainer(tag)) {
      node.childNodes.forEach(visit);
      return;
    }
    node.childNodes.forEach(visit);
  };

  editorElement.childNodes.forEach(visit);

  if (!result) {
    result = {
      node: editorElement,
      offset: editorElement.childNodes.length,
    };
  }

  const range = document.createRange();
  try {
    range.setStart(result.node, result.offset);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch {
    return false;
  }
};

const findNodeStartPosition = (container) => {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const firstText = walker.nextNode();
  if (firstText) {
    return { node: firstText, offset: 0 };
  }
  return { node: container, offset: 0 };
};

const findNodeEndPosition = (container) => {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let lastText = null;
  let node = walker.nextNode();
  while (node) {
    lastText = node;
    node = walker.nextNode();
  }
  if (lastText) {
    return { node: lastText, offset: lastText.textContent?.length ?? 0 };
  }
  return { node: container, offset: container.childNodes.length };
};

const findOffsetInLineContent = (lineContainer, targetOffset) => {
  let remaining = Math.max(0, targetOffset);
  let result = null;

  const visit = (node) => {
    if (result) {
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = normalizeRunText(node.textContent ?? "");
      if (remaining <= text.length) {
        result = {
          node,
          offset: Math.min(remaining, node.textContent?.length ?? 0),
        };
        return;
      }
      remaining -= text.length;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const tag = node.tagName?.toLowerCase();
    if (tag === "br") {
      return;
    }
    if (isBlockContainer(tag)) {
      node.childNodes.forEach(visit);
      return;
    }
    node.childNodes.forEach(visit);
  };

  lineContainer.childNodes.forEach(visit);
  return result || { node: lineContainer, offset: lineContainer.childNodes.length };
};

const findCharacterOffsetPosition = (editorElement, targetOffset) => {
  const lineDivs = getLineAlignDivs(editorElement);
  if (lineDivs.length > 0) {
    let remaining = Math.max(0, targetOffset);
    for (let lineIndex = 0; lineIndex < lineDivs.length; lineIndex += 1) {
      if (lineIndex > 0) {
        if (remaining === 0) {
          return findNodeStartPosition(lineDivs[lineIndex]);
        }
        remaining -= 1;
      }

      const lineLength = getLinePlainTextLength(lineDivs[lineIndex]);
      if (remaining <= lineLength) {
        return findOffsetInLineContent(lineDivs[lineIndex], remaining);
      }
      remaining -= lineLength;
    }
    return findNodeEndPosition(lineDivs[lineDivs.length - 1]);
  }

  let remaining = Math.max(0, targetOffset);
  let result = null;

  const placeAtHardBreak = (node, beforeBreak) => {
    const parent = node.parentNode;
    if (!parent) {
      return;
    }
    const index = Array.prototype.indexOf.call(parent.childNodes, node);
    result = {
      node: parent,
      offset: beforeBreak ? index : index + 1,
    };
  };

  const visit = (node) => {
    if (result) {
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      const text = normalizeRunText(node.textContent ?? "");
      if (remaining <= text.length) {
        result = {
          node,
          offset: Math.min(remaining, node.textContent?.length ?? 0),
        };
        return;
      }
      remaining -= text.length;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const tag = node.tagName?.toLowerCase();
    if (tag === "br") {
      if (isHardBreakElement(node)) {
        if (remaining === 0) {
          placeAtHardBreak(node, true);
          return;
        }
        remaining -= 1;
        if (remaining === 0) {
          placeAtHardBreak(node, false);
        }
      }
      return;
    }
    if (isBlockContainer(tag)) {
      node.childNodes.forEach(visit);
      return;
    }
    node.childNodes.forEach(visit);
  };

  editorElement.childNodes.forEach(visit);
  return result || { node: editorElement, offset: editorElement.childNodes.length };
};

export const setSelectionCharacterRange = (editorElement, startOffset, endOffset) => {
  const selection = window.getSelection();
  if (!selection || !editorElement) {
    return false;
  }

  const safeStart = Math.max(0, Math.min(startOffset, endOffset));
  const safeEnd = Math.max(0, Math.max(startOffset, endOffset));
  const start = findCharacterOffsetPosition(editorElement, safeStart);
  const end = findCharacterOffsetPosition(editorElement, safeEnd);
  if (!start || !end) {
    return false;
  }

  const range = document.createRange();
  try {
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch {
    return false;
  }
};

export const canonicalizeParagraphEditorDom = (editorElement, options = {}) => {
  if (!editorElement) {
    return {
      runs: [createEmptyRun()],
      lineAligns: undefined,
    };
  }
  const selection = window.getSelection();
  const isFocused = Boolean(selection?.anchorNode && editorElement.contains(selection.anchorNode));
  const caretOffset = isFocused ? getCaretCharacterOffset(editorElement) : null;
  const { runs, lineAligns } = htmlToParagraphEditorContent(editorElement);
  const defaultAlign = resolveParagraphAlign({ align: options.paragraphAlign });
  const compactedLineAligns = compactLineAligns(lineAligns, splitRunsIntoLines(runs).length, defaultAlign);
  const canonicalHtml = runsToEditorHtml(runs, {
    paragraphAlign: options.paragraphAlign,
    lineAligns: compactedLineAligns || lineAligns,
  });

  if (editorElement.innerHTML !== canonicalHtml) {
    editorElement.innerHTML = canonicalHtml;
    if (caretOffset != null) {
      setCaretCharacterOffset(editorElement, caretOffset);
    }
  }

  return {
    runs,
    lineAligns: compactedLineAligns,
  };
};

export const paragraphPlainText = (block) =>
  normalizeParagraphBlock(block).runs.map((run) => run.text).join("");

export const resolveParagraphAlign = (block) =>
  block?.align === "left" || block?.align === "center" || block?.align === "justify"
    ? block.align
    : DEFAULT_PARAGRAPH_ALIGN;

export const paragraphBlockToHtml = (block) => {
  const normalized = normalizeParagraphBlock(block);
  const lines = splitRunsIntoLines(normalized.runs);
  const lineAligns = normalized.lineAligns || [];
  const useLineLayout = lines.length > 1 || lineAligns.some((value) => normalizeLineAlignValue(value));

  if (!useLineLayout) {
    return runsToPublicHtml(normalized.runs);
  }

  return lines
    .map((lineRuns, index) => {
      const align = resolveLineAlign(normalized, index);
      const inner = runsToPublicHtml(lineRuns);
      return `<div style="text-align:${align};display:block;width:100%">${inner || "<br>"}</div>`;
    })
    .join("");
};

export const getParagraphContainerSx = (block, baseSx) => ({
  ...baseSx,
  textAlign: usesLineAlignLayout(block) ? undefined : resolveParagraphAlign(block),
  display: "block",
  boxSizing: "border-box",
  width: "100%",
  maxWidth: "100%",
});

export const normalizeTextMarks = (marks) => {
  if (!marks || typeof marks !== "object") {
    return undefined;
  }
  const normalized = {};
  if (marks.weight === "bold") {
    normalized.weight = "bold";
  }
  if (marks.style === "italic") {
    normalized.style = "italic";
  }
  const color = normalizeMarkColor(marks.color);
  if (color) {
    normalized.color = color;
  }
  const fontSize = normalizeFontSize(marks.fontSize);
  if (fontSize) {
    normalized.fontSize = fontSize;
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

export const normalizeParagraphBlockFromApi = (block) => {
  if (!block || block.type !== "paragraph") {
    return block;
  }
  let sourceRuns = [];
  if (Array.isArray(block.runs) && block.runs.length > 0) {
    sourceRuns = block.runs;
  } else if (block.text !== undefined) {
    sourceRuns = [{ text: block.text ?? "", marks: block.marks || undefined }];
  }
  const runs = sourceRuns
    .map((run) => {
      const marks = normalizeTextMarks(run.marks);
      return {
        text: stripSoftWrapNewlineArtifacts(normalizeRunText(run.text)),
        ...(marks ? { marks } : {}),
      };
    })
    .filter((run) => run.text.length > 0);
  const normalized = {
    type: "paragraph",
    runs: runs.length > 0 ? runs : [{ text: "" }],
  };
  if (block.align === "left" || block.align === "center" || block.align === "justify") {
    normalized.align = block.align;
  }
  if (Array.isArray(block.lineAligns) && block.lineAligns.length > 0) {
    normalized.lineAligns = block.lineAligns.map((value) => normalizeLineAlignValue(value));
  }
  return normalized;
};

export const normalizeImageBlockFromApi = (block) => ({
  type: "image",
  objectKey: String(block.objectKey ?? ""),
  publicUrl: String(block.publicUrl ?? ""),
  alt: block.alt == null ? null : String(block.alt),
});

export const normalizeContentBlockFromApi = (block) => {
  if (!block || typeof block !== "object" || !block.type) {
    return block;
  }
  if (block.type === "paragraph") {
    return normalizeParagraphBlockFromApi(block);
  }
  if (block.type === "image") {
    return normalizeImageBlockFromApi(block);
  }
  return block;
};

export const insertHardBreakAtSelection = (editorElement) => {
  if (!editorElement) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!editorElement.contains(range.commonAncestorContainer)) {
    return false;
  }
  range.deleteContents();
  const hardBreak = document.createElement("br");
  hardBreak.setAttribute(WCP_HARD_BREAK_ATTR, WCP_HARD_BREAK_VALUE);
  range.insertNode(hardBreak);
  range.setStartAfter(hardBreak);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
};

const findLineAlignDiv = (node, editorElement) => {
  let current = node;
  while (current && current !== editorElement) {
    if (isLineAlignDiv(current)) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
};

export const ensureEditorLineLayout = (editorElement, paragraphAlign) => {
  const existing = getLineAlignDivs(editorElement);
  if (existing.length > 0) {
    return existing;
  }
  const align = resolveParagraphAlign({ align: paragraphAlign });
  const lineDiv = document.createElement("div");
  lineDiv.setAttribute(WCP_LINE_ALIGN_ATTR, align);
  lineDiv.style.textAlign = align;
  while (editorElement.firstChild) {
    lineDiv.appendChild(editorElement.firstChild);
  }
  editorElement.appendChild(lineDiv);
  return [lineDiv];
};

export const insertLineBreakAtSelection = (editorElement, options = {}) => {
  if (!editorElement) {
    return false;
  }
  const selection = window.getSelection();
  if (!selection?.rangeCount) {
    return false;
  }
  const range = selection.getRangeAt(0);
  if (!editorElement.contains(range.commonAncestorContainer)) {
    return false;
  }

  ensureEditorLineLayout(editorElement, options.paragraphAlign);
  const lineDiv = findLineAlignDiv(range.startContainer, editorElement);
  if (!lineDiv) {
    return false;
  }

  if (!range.collapsed) {
    range.deleteContents();
  }

  const tailRange = document.createRange();
  tailRange.selectNodeContents(lineDiv);
  tailRange.setStart(range.startContainer, range.startOffset);
  const tailContents = tailRange.extractContents();
  const lineAlign = normalizeLineAlignValue(lineDiv.getAttribute(WCP_LINE_ALIGN_ATTR)) || DEFAULT_PARAGRAPH_ALIGN;

  const newLineDiv = document.createElement("div");
  newLineDiv.setAttribute(WCP_LINE_ALIGN_ATTR, lineAlign);
  newLineDiv.style.textAlign = lineAlign;
  if (tailContents.childNodes.length > 0) {
    newLineDiv.appendChild(tailContents);
  }

  lineDiv.parentNode?.insertBefore(newLineDiv, lineDiv.nextSibling);
  placeCaretAtNodeStart(newLineDiv, selection);
  return true;
};

export const getRangeCharacterOffset = (editorElement, container, offset) => {
  if (!editorElement || !container) {
    return null;
  }

  const measureRange = document.createRange();
  try {
    measureRange.setStart(container, offset);
    measureRange.collapse(true);
  } catch {
    return null;
  }

  if (!editorElement.contains(measureRange.startContainer)) {
    return null;
  }

  const lineDivs = getLineAlignDivs(editorElement);
  if (lineDivs.length > 0) {
    let charOffset = 0;
    for (let lineIndex = 0; lineIndex < lineDivs.length; lineIndex += 1) {
      const lineDiv = lineDivs[lineIndex];
      if (lineDiv.contains(container) || lineDiv === container) {
        const innerRange = document.createRange();
        innerRange.selectNodeContents(lineDiv);
        innerRange.setEnd(container, offset);
        const probe = document.createElement("div");
        probe.appendChild(innerRange.cloneContents());
        charOffset += runsToPlainOffsetText(htmlToParagraphEditorContent(probe).runs).length;
        return charOffset;
      }
      charOffset += getLinePlainTextLength(lineDiv);
      if (lineIndex < lineDivs.length - 1) {
        charOffset += 1;
      }
    }
    return charOffset;
  }

  const preRange = document.createRange();
  preRange.selectNodeContents(editorElement);
  preRange.setEnd(container, offset);
  const probe = document.createElement("div");
  probe.appendChild(preRange.cloneContents());
  return runsToPlainOffsetText(htmlToRuns(probe)).length;
};

const getLineIndexForCharacterOffset = (plainText, characterOffset) => {
  const safeOffset = Math.max(0, Math.min(characterOffset, plainText.length));
  return plainText.slice(0, safeOffset).split("\n").length - 1;
};

export const getSelectedLineIndices = (editorElement) => {
  const selection = window.getSelection();
  if (!selection?.rangeCount || !editorElement) {
    return [];
  }
  const range = selection.getRangeAt(0);
  if (!editorElement.contains(range.commonAncestorContainer) || range.collapsed) {
    return [];
  }

  const startOffset = getRangeCharacterOffset(editorElement, range.startContainer, range.startOffset);
  const endOffset = getRangeCharacterOffset(editorElement, range.endContainer, range.endOffset);
  if (typeof startOffset !== "number" || typeof endOffset !== "number") {
    return [];
  }

  const plainText = runsToPlainOffsetText(htmlToParagraphEditorContent(editorElement).runs);
  const startLine = getLineIndexForCharacterOffset(plainText, startOffset);
  const endLine = getLineIndexForCharacterOffset(plainText, endOffset);
  const indices = [];
  for (let index = Math.min(startLine, endLine); index <= Math.max(startLine, endLine); index += 1) {
    indices.push(index);
  }
  return indices;
};

const runHasPersistableContent = (text) =>
  String(text ?? "").replace(/\n/g, "").trim().length > 0 || String(text ?? "").includes("\n");

export const sanitizeParagraphRunsForSave = (block) => {
  const normalized = normalizeParagraphBlock(block);
  const runs = mergeAdjacentRuns(
    normalized.runs
      .map((run) => ({
        text: normalizeRunText(run.text),
        marks: normalizeTextMarks(run.marks),
      }))
      .filter((run) => run.text.length > 0)
  )
    .map((run) => {
      const marks = normalizeTextMarks(run.marks);
      return {
        text: run.text,
        ...(marks ? { marks } : {}),
      };
    });
  const payload = {
    type: "paragraph",
    runs: runs.filter((run) => runHasPersistableContent(run.text)),
  };
  const align = normalizeParagraphAlign(normalized.align);
  if (align) {
    payload.align = align;
  }
  const defaultAlign = resolveParagraphAlign(normalized);
  const compactedLineAligns = compactLineAligns(
    normalized.lineAligns,
    splitRunsIntoLines(normalized.runs).length,
    defaultAlign
  );
  if (compactedLineAligns) {
    payload.lineAligns = compactedLineAligns;
  }
  return payload;
};
